/**
 * Mathematics — stages S9, S10 and the two S12 math environments.
 *
 * Ported from (`escapeHtml`, `renderMath`, `processInlineMath`
 * `processDisplayMath`) and (`equation` / `align` inside processEnvironments).
 *
 * Spec: docs/spec/00-MASTER-SPEC.md §3.7 (R-121…R-125), §3.10 rows R-292 / R-293.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * PIPELINE POSITION — load-bearing (spec §1)
 *
 *   S8  processFootnotesEarly  ⟶ note bodies are already held out of the text, so they
 *                                NEVER reach any of this (R-139: `$x^2$` inside a note
 *                                publishes literally)
 *   S9  processDisplayMath     ⟵ `$$…$$` first, then `\[…\]`
 *   S10 processInlineMath      ⟵ `$…$`
 *   S11 processCommands        ⟶ (`\$` → `$`) runs HERE, far too late to protect
 *                                anything — see DF-5 below
 *   S12 processEnvironments    ⟵ equation (rule 13) and align (rule 14)
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * DF-5 — `\$` IS SWALLOWED BY INLINE MATH. REPRODUCED DELIBERATELY.
 *
 * `processInlineMath` scans for `$` with no awareness of the backslash in front of it, and
 * it runs at S10 — one stage before the `\$ → $` rule at that was meant to protect
 * it. So the text BETWEEN two escaped dollars is consumed as math, fails to parse, and is
 * replaced by a KaTeX error span. The document loses that text.
 *
 *   `Prix \$5 et \$10.`  →  `Prix \<span class="math-inline"><span class="katex-error" …>5 et \</span></span>10.`
 *
 * It is worse than "text disappears": the KaTeX error span carries the swallowed text as
 * markup, and with an odd number of escaped dollars across a paragraph boundary the opening
 * `<span>` and its `</span>` land in DIFFERENT paragraphs, straddling `</p><p>`. See
 * tests/golden/typography/05-dollar-escape.A.html for that shape in full.
 *
 * Do not mask `\$` before scanning. That is the proposed fix in DF-5 and it is deferred.
 */

import katex from 'katex'

/**
 *. Ported for completeness; on the live path it is applied to NOTHING (G-28).
 *
 * Its only caller is the `catch` in {@link renderMath}, which `throwOnError: false` makes
 * near-unreachable — KaTeX returns an error span rather than throwing. Document text is
 * never HTML-escaped anywhere in this engine (DF-9): a `<b>` a translator types is emitted
 * as live markup. That is the current production behaviour and it is deliberately kept.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * — the single KaTeX entry point for every math construct.
 *
 * The options are EXACTLY these three and no others (R-121). In particular there is no
 * `macros`, no `trust`, no `strict`, no `errorColor`, no `maxSize` — so KaTeX's own
 * defaults apply (`strict: "warn"`, `trust: false`), no document macro is visible to the
 * math renderer, and `\href`-style trusted commands are refused. Adding an option here
 * would change the rendered markup of every formula in the corpus.
 *
 * `throwOnError: false` is what makes the `math-error` branch below near-unreachable:
 * KaTeX returns `<span class="katex-error" title="ParseError: …" style="color:#cc0000">`
 * containing the offending source instead of throwing. That error span — not
 * `<span class="math-error">` — is what the goldens contain.
 */
export function renderMath(latex: string, displayMode: boolean = false): string {
  try {
    return katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      output: 'html',
    })
  } catch {
    // Near-unreachable (see above), and the only caller of escapeHtml (G-28).
    return `<span class="math-error">${escapeHtml(latex)}</span>`
  }
}

/**
 * S10 / R-121 — inline `$…$`.
 *
 * `[^$]+` cannot cross a `$`, so `$a$ text $b$` yields two separate formulas and an
 * unmatched `$` is left alone. `$$` on its own matches neither this nor the display rule
 * (both need at least one character between the delimiters) and survives as literal text.
 *
 * Runs AFTER {@link processDisplayMath}, so any `$$…$$` has already been consumed.
 *
 * DF-5 lives here: nothing exempts `\$`. See the module header.
 */
export function processInlineMath(text: string): string {
  // R-121
  return text.replace(/\$([^$]+)\$/g, (_match: string, math: string): string => {
    return `<span class="math-inline">${renderMath(math, false)}</span>`
  })
}

/**
 * S9 / R-122 + R-123 — display math. Two rules, in this order.
 *
 * R-123's `[^\]]+` means a `]` inside the body terminates the scan, and since the regex then
 * requires `\]` immediately, the whole construct simply fails to match and leaks as literal
 * LaTeX: `\[ x[1] = 2 \]` publishes verbatim. Verified against the oracle.
 *
 * Both bodies are passed to KaTeX untrimmed — the leading and trailing spaces of
 * `\[ a^2 \]` reach the renderer.
 */
export function processDisplayMath(text: string): string {
  // R-122 — `$$…$$` first, so the inline rule never sees a display delimiter.
  let out = text.replace(/\$\$([^$]+)\$\$/g, (_match: string, math: string): string => {
    return `<div class="math-display">${renderMath(math, true)}</div>`
  })

  // R-123 — `\[…\]`
  out = out.replace(/\\\[([^\]]+)\\\]/g, (_match: string, math: string): string => {
    return `<div class="math-display">${renderMath(math, true)}</div>`
  })

  return out
}

/**
 * S12 rule 13 / R-124 / R-292 — the `equation` environment.
 *
 * ASYMMETRIC BY CONSTRUCTION: `\*?` is applied independently to the begin and the end, so
 * `\begin{equation}…\end{equation*}` matches, and so does the reverse. Reproduced.
 *
 * Unlike `$$…$$`, the body IS trimmed before rendering (`math.trim()`).
 *
 * Must be applied by processEnvironments at its position — after `description` (R-291) and
 * before `tabular` (R-294). Order matters: it is the environment stage, so it runs after
 * processCommands has already rewritten the surrounding text.
 */
export function processEquationEnvironment(html: string): string {
  // R-124
  return html.replace(
    /\\begin\{equation\*?\}([\s\S]*?)\\end\{equation\*?\}/g,
    (_match: string, math: string): string => {
      return `<div class="math-display">${renderMath(math.trim(), true)}</div>`
    },
  )
}

/**
 * S12 rule 14 / R-125 / R-293 — the `align` environment.
 *
 * The body is re-wrapped in `aligned` before rendering, because KaTeX has no top-level
 * `align`. Same `\*?` asymmetry as {@link processEquationEnvironment}.
 *
 * Note the `\\` row separators inside the body survive to KaTeX untouched: S13
 * (`\\` → `<br>`) runs after the environment stage.
 */
export function processAlignEnvironment(html: string): string {
  // R-125
  return html.replace(
    /\\begin\{align\*?\}([\s\S]*?)\\end\{align\*?\}/g,
    (_match: string, math: string): string => {
      return `<div class="math-display">${renderMath('\\begin{aligned}' + math.trim() + '\\end{aligned}', true)}</div>`
    },
  )
}
